import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { IconAlertSquareRounded, IconChevronLeft, IconEye, IconEyeOff } from '@tabler/icons-react';
import { signUp } from 'aws-amplify/auth';
import ConfirmSignUp from '../ConfirmSignUp/ConfirmSignUp';
import PropTypes from 'prop-types';
import PasswordRequirements from '../../PasswordRequirements/PasswordRequirements';

// import flatColorIconsGoogle from '../../../assets/images/flat-color-icons_google.png';

const IndividualRegisterSchema = Yup.object().shape({
  fullName: Yup.string()
    .matches(/^[a-zA-Z\s]+$/, 'Full name can only contain letters and spaces')
    .required('Full name field is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email address field is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
    )
    .required('Password field is required'),
  termsAccepted: Yup.boolean()
    .oneOf([true], 'You must agree to the Terms of Use and Privacy Policy')
    .required('You must agree to the Terms of Use and Privacy Policy'),
});

function IndividualRegister({ priceID }) {
  const [showPassword, setShowPassword] = useState(false);
  const [nextStep, setNextStep] = useState(null);
  const [userCredentials, setUserCredentials] = useState(null);
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/landing');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const userAttributes = {
        email: values.email,
        name: values.fullName,
      };

      if (priceID) {
        userAttributes['custom:selected_price_id'] = priceID;
      }

      const { nextStep } = await signUp({
        username: values.email,
        password: values.password,
        options: {
          userAttributes,
          autoSignIn: true,
        },
      });

      setNextStep(nextStep);
      setUserCredentials({
        username: values.email,
        password: values.password,
        email: values.email,
      });
    } catch (err) {
      let errorMessage = 'An unexpected error occurred. Please try again.';

      switch (err.name) {
        case 'UsernameExistsException':
          errorMessage = 'An account with this email address already exists';
          break;
        case 'InvalidParameterException':
          errorMessage = 'Invalid registration parameters';
          break;
        case 'InvalidPasswordException':
          errorMessage = 'Password does not meet requirements';
          break;
        case 'UserLambdaValidationException':
          errorMessage = err.message.split('|')[1];
          break;
        default:
          errorMessage = 'An unexpected error occurred. Please try again.';
      }

      setStatus({ error: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  if (nextStep?.signUpStep === 'CONFIRM_SIGN_UP' && userCredentials) {
    return (
      <ConfirmSignUp
        userCredentials={userCredentials}
        onGoBack={() => {
          setNextStep(null);
          setUserCredentials(null);
        }}
      />
    );
  }

  return (
    <>
      <div className="personal_info">
        <Link to="/join-us">
          <IconChevronLeft /> Back
        </Link>
        <p className="signin-text mb-0">
          <Link to={'/'} style={{ textDecoration: 'none', color: 'inherit' }}>
            Already have an account?{' '}
            <span className="text-primary">&nbsp;Sign In</span>
          </Link>
        </p>
      </div>
      <Formik
        initialValues={{
          fullName: '',
          email: '',
          password: '',
          termsAccepted: false,
        }}
        validationSchema={IndividualRegisterSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, status, isSubmitting, values }) => (
          <Form className="cst-form">
            <h1 className="mb-4">Register Individual Account!</h1>
            {/* <p>
              For the purpose of industry regulation, your details are required.
            </p> */}

            <div className="mb-lg-4 mb-3">
              <label htmlFor="fullName" className="form-label">
                Your Full Name*
              </label>
              <Field
                type="text"
                className={`form-control ${touched.fullName && errors.fullName ? 'is-invalid border-danger' : ''}`}
                id="fullName"
                name="fullName"
                autoComplete="off"
                placeholder="Joseph Smith"
              />
              {touched.fullName && errors.fullName && (
                <div className="invalid-feedback ">{errors.fullName}</div>
              )}
            </div>

            <div className="mb-lg-4 mb-3">
              <label htmlFor="email" className="form-label">
                Email address*
              </label>
              <Field
                type="email"
                className={`form-control ${touched.email && errors.email ? 'is-invalid border-danger' : ''}`}
                id="email"
                name="email"
                autoComplete="off"
                placeholder="name@example.com"
              />
              {touched.email && errors.email && (
                <div className="invalid-feedback ">{errors.email}</div>
              )}
            </div>

            <div className="mb-lg-4 mb-3">
              <label htmlFor="password" className="form-label">
                Create password*
              </label>
              <div className="input-with-icon create-pass">
                <Field
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control pr-12 ${touched.password && errors.password ? 'is-invalid border-danger' : ''}`}
                  id="password"
                  name="password"
                  autoComplete="off"
                  placeholder="Password"
                />
                {values.password && (
                  <span
                    className="password-toggle-icon"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{ cursor: 'pointer' }}
                  >
                    {showPassword ? (
                      <IconEyeOff size={20} />
                    ) : (
                      <IconEye size={20} />
                    )}
                  </span>
                )}
              </div>
              {values.password && (
                <PasswordRequirements 
                  password={values.password} 
                  className="mt-3"
                />
              )}
              {touched.password && errors.password && (
                <div className="invalid-feedback d-block">{errors.password}</div>
              )}
            </div>

            <div className="mb-lg-4 mb-3">
              <div className="d-flex align-items-start gap-2">
                <Field
                  type="checkbox"
                  name="termsAccepted"
                  id="termsAccepted"
                  disabled={isSubmitting}
                  className={`cursor-pointer mt-1 ${
                    touched.termsAccepted && errors.termsAccepted 
                      ? 'is-invalid' 
                      : ''
                  }`}
                />
                <label
                  htmlFor="termsAccepted"
                  className="text-sm cursor-pointer"
                  style={{ lineHeight: '1.5' }}
                >
                  By creating an account, I agree to PrescriptSure's{' '}
                  <a
                    href={`${process.env.REACT_APP_PUBLIC_WEBSITE_URL}/terms-of-use`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Use
                  </a>
                  ,{' '}
                  <a
                    href={`${process.env.REACT_APP_PUBLIC_WEBSITE_URL}/privacy-policy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                  ,{' '}
                  <a
                    href={`${process.env.REACT_APP_PUBLIC_WEBSITE_URL}/cookie-policy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Cookie Policy
                  </a>
                  , and{' '}
                  <a
                    href={`${process.env.REACT_APP_PUBLIC_WEBSITE_URL}/accessibility-policy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Accessibility Policy
                  </a>
                  .
                </label>
              </div>
              {touched.termsAccepted && errors.termsAccepted && (
                <div className="text-danger small mt-1">
                  {errors.termsAccepted}
                </div>
              )}
            </div>

            {status && status.error && (
              <div className="alert alert-danger" role="alert">
                <span className="px-2">
                  <IconAlertSquareRounded />
                </span>
                {status.error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span 
                    className="spinner-border spinner-border-sm me-2" 
                    role="status" 
                    aria-hidden="true"
                  />
                  <span>Registering...</span>
                </>
              ) : (
                'Register Account'
              )}
            </button>

            {/* <div className="or-devider">
              <span>OR</span>
            </div>

            <button
              type="button"
              className="btn register-google-btn"
              onClick={() => signInWithRedirect({ provider: 'Google' })}
            >
              <img
                className="me-3"
                src={flatColorIconsGoogle}
                alt="google icon"
              />
              Register with Google
            </button> */}
          </Form>
        )}
      </Formik>
    </>
  );
}

IndividualRegister.propTypes = {
  priceID: PropTypes.string.isRequired,
};

export default IndividualRegister;
